import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import { createTopic } from '../../services/forumService';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';

const CATEGORIES = [
    { value: 'GENERAL', label: 'Discussion Générale', icon: '💬', color: '#a78bfa', desc: 'Échanges libres entre investisseurs' },
    { value: 'MACROECO', label: 'Macroéconomie & Finance', icon: '🌍', color: '#f472b6', desc: 'Taux, inflation, politiques monétaires' },
    { value: 'BRVM', label: 'BRVM (Indices, Actus)', icon: '📈', color: '#34d399', desc: 'Marchés, indices, actualités BRVM' },
    { value: 'ACTION_SPECIFIQUE', label: 'Action Spécifique', icon: '🏢', color: '#38bdf8', desc: 'Analyse d\'une valeur cotée' },
];

const CreateTopicScreen: React.FC = () => {
    const navigate = useNavigate();
    const [title, setTitle] = useState('');
    const [category, setCategory] = useState('GENERAL');
    const [stockSymbol, setStockSymbol] = useState('');
    const [content, setContent] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [societes, setSocietes] = useState<{ symbole: string; nom_societe: string }[]>([]);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const { user } = useAuth();

    const textareaRef = React.useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        const fetchSocietes = async () => {
            try {
                const token = localStorage.getItem('access_token');
                const response = await fetch(`${API_URL}/v1/stocks/active`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    setSocietes(data);
                }
            } catch (err) {
                console.error("Failed to load stocks list", err);
            }
        };
        fetchSocietes();
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!title.trim() || !content.trim()) return;
        try {
            setSubmitting(true);
            const newTopic = await createTopic(
                title, category, content,
                category === 'ACTION_SPECIFIQUE' ? stockSymbol : undefined
            );
            navigate(`/forum/topic/${newTopic.id}`);
        } catch (err) {
            console.error("Erreur lors de la création du sujet", err);
            alert("Une erreur est survenue lors de la création du sujet.");
        } finally {
            setSubmitting(false);
        }
    };

    const insertText = (before: string, after: string = '') => {
        if (!textareaRef.current) return;
        const start = textareaRef.current.selectionStart;
        const end = textareaRef.current.selectionEnd;
        const text = textareaRef.current.value;
        const selectedText = text.substring(start, end);
        const newText = text.substring(0, start) + before + selectedText + after + text.substring(end);
        setContent(newText);
        
        // Restore focus and selection
        setTimeout(() => {
            if (textareaRef.current) {
                textareaRef.current.focus();
                textareaRef.current.setSelectionRange(start + before.length, end + before.length);
            }
        }, 0);
    };

    const activeCat = CATEGORIES.find(c => c.value === category) || CATEGORIES[0];
    const charCount = content.length;

    return (
        <SiteLayout>
            <Header icon="forum" title="Nouvelle Discussion" />
            <div className='content-area'>
                <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>

                    {/* Back */}
                    <button
                        onClick={() => navigate('/forum')}
                        style={{
                            background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-color)',
                            color: 'var(--text-muted)', cursor: 'pointer', marginBottom: '24px',
                            fontWeight: 600, padding: '8px 16px', borderRadius: '10px',
                            fontSize: '13px', transition: 'all 0.2s ease',
                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                        }}
                        onMouseOver={e => { e.currentTarget.style.borderColor = '#8b5cf6'; e.currentTarget.style.color = '#8b5cf6'; }}
                        onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--border-color)'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                    >
                        ← Retour aux Discussions
                    </button>

                    {/* Header */}
                    <div style={{
                        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(168, 85, 247, 0.06) 100%)',
                        borderRadius: '16px', padding: '28px 32px', marginBottom: '28px',
                        border: '1px solid rgba(99, 102, 241, 0.12)',
                        position: 'relative', overflow: 'hidden',
                    }}>
                        <div style={{
                            position: 'absolute', top: '-30px', right: '-30px', width: '140px', height: '140px',
                            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.15), transparent 70%)',
                            borderRadius: '50%', pointerEvents: 'none',
                        }} />
                        <h1 style={{
                            color: 'var(--text-main)', fontSize: '26px', margin: '0 0 8px 0',
                            fontWeight: 800, letterSpacing: '-0.3px',
                        }}>
                            ✏️ Créer une nouvelle discussion
                        </h1>
                        <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '14px' }}>
                            Partagez vos analyses, questions ou convictions avec la communauté.
                        </p>
                    </div>

                    {/* Form Card */}
                    <form onSubmit={handleSubmit}>
                        <div style={{
                            background: 'rgba(255,255,255,0.02)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '16px', padding: '32px',
                        }}>
                            {/* Title */}
                            <div style={{ marginBottom: '24px' }}>
                                <label style={{
                                    display: 'block', marginBottom: '8px', color: 'var(--text-main)',
                                    fontWeight: 700, fontSize: '14px',
                                }}>
                                    Titre du sujet
                                </label>
                                <input
                                    type="text" value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="De quoi voulez-vous discuter ?"
                                    required maxLength={250}
                                    style={{
                                        width: '100%', padding: '14px 16px',
                                        backgroundColor: 'rgba(0,0,0,0.15)',
                                        color: 'var(--text-main)',
                                        border: '1px solid var(--border-color)',
                                        borderRadius: '12px', fontSize: '15px',
                                        outline: 'none', transition: 'border-color 0.2s ease',
                                        boxSizing: 'border-box',
                                    }}
                                    onFocus={e => e.currentTarget.style.borderColor = '#8b5cf6'}
                                    onBlur={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
                                />
                            </div>

                            {/* Category Cards */}
                            <div style={{ marginBottom: '24px' }}>
                                <label style={{
                                    display: 'block', marginBottom: '12px', color: 'var(--text-main)',
                                    fontWeight: 700, fontSize: '14px',
                                }}>
                                    Catégorie
                                </label>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                                    {CATEGORIES.map(cat => {
                                        const isActive = category === cat.value;
                                        return (
                                            <div
                                                key={cat.value}
                                                onClick={() => {
                                                    setCategory(cat.value);
                                                    if (cat.value !== 'ACTION_SPECIFIQUE') setStockSymbol('');
                                                }}
                                                style={{
                                                    padding: '14px 16px',
                                                    borderRadius: '12px',
                                                    border: `2px solid ${isActive ? cat.color : 'var(--border-color)'}`,
                                                    background: isActive ? `${cat.color}12` : 'rgba(255,255,255,0.01)',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease',
                                                    display: 'flex', alignItems: 'center', gap: '12px',
                                                }}
                                                onMouseOver={e => { if (!isActive) e.currentTarget.style.borderColor = `${cat.color}66`; }}
                                                onMouseOut={e => { if (!isActive) e.currentTarget.style.borderColor = 'var(--border-color)'; }}
                                            >
                                                <span style={{ fontSize: '22px' }}>{cat.icon}</span>
                                                <div>
                                                    <div style={{ color: isActive ? cat.color : 'var(--text-main)', fontWeight: 700, fontSize: '13px' }}>
                                                        {cat.label}
                                                    </div>
                                                    <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginTop: '2px' }}>
                                                        {cat.desc}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Stock Selector */}
                            {category === 'ACTION_SPECIFIQUE' && (
                                <div style={{
                                    marginBottom: '24px', padding: '20px',
                                    background: 'rgba(56, 189, 248, 0.06)',
                                    border: '1px solid rgba(56, 189, 248, 0.15)',
                                    borderRadius: '12px',
                                    animation: 'fadeIn 0.3s ease',
                                }}>
                                    <label style={{
                                        display: 'block', marginBottom: '8px', color: '#38bdf8',
                                        fontWeight: 700, fontSize: '14px',
                                    }}>
                                        📊 Sélectionner une valeur
                                    </label>
                                    <select
                                        value={stockSymbol}
                                        onChange={(e) => setStockSymbol(e.target.value)}
                                        required={category === 'ACTION_SPECIFIQUE'}
                                        style={{
                                            width: '100%', padding: '12px 16px',
                                            backgroundColor: '#1a2332',
                                            color: '#e2e8f0',
                                            border: '1px solid rgba(56, 189, 248, 0.3)',
                                            borderRadius: '10px', fontSize: '14px',
                                            outline: 'none', boxSizing: 'border-box',
                                        }}
                                    >
                                        <option value="" style={{ backgroundColor: '#1a2332', color: '#e2e8f0' }}>-- Choisir une valeur --</option>
                                        {societes.map(s => (
                                            <option key={s.symbole} value={s.symbole} style={{ backgroundColor: '#1a2332', color: '#e2e8f0' }}>{s.nom_societe || s.symbole} ({s.symbole})</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Message */}
                            <div style={{ marginBottom: '24px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                    <label style={{ color: 'var(--text-main)', fontWeight: 700, fontSize: '14px' }}>
                                        Votre message
                                    </label>
                                    <span style={{ color: charCount > 2000 ? '#f43f5e' : 'var(--text-muted)', fontSize: '12px' }}>
                                        {charCount} caractères
                                    </span>
                                </div>
                                <div style={{ position: 'relative' }}>
                                    {/* Toolbar */}
                                    <div style={{
                                        display: 'flex', gap: '4px', marginBottom: '8px',
                                        padding: '4px', borderRadius: '8px',
                                        background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-color)',
                                    }}>
                                        {[
                                            { label: 'B', tag: '**', after: '**', title: 'Gras' },
                                            { label: 'I', tag: '*', after: '*', title: 'Italique' },
                                            { label: '•', tag: '- ', after: '', title: 'Liste' },
                                        ].map((tool, i) => (
                                            <button
                                                key={i} type="button"
                                                onClick={() => insertText(tool.tag, tool.after)}
                                                title={tool.title}
                                                style={{
                                                    width: '32px', height: '32px', borderRadius: '6px', border: 'none',
                                                    background: 'transparent', color: 'var(--text-main)',
                                                    cursor: 'pointer', fontWeight: 800, fontSize: '13px',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}
                                                onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                                                onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                                            >
                                                {tool.label}
                                            </button>
                                        ))}
                                        <div style={{ width: '1px', background: 'var(--border-color)', margin: '4px 2px' }} />
                                        <button
                                            type="button"
                                            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                            title="Emojis"
                                            style={{
                                                width: '32px', height: '32px', borderRadius: '6px', border: 'none',
                                                background: showEmojiPicker ? 'rgba(139, 92, 246, 0.2)' : 'transparent',
                                                color: 'var(--text-main)', cursor: 'pointer', fontSize: '16px',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}
                                            onMouseOver={e => e.currentTarget.style.background = showEmojiPicker ? 'rgba(139, 92, 246, 0.2)' : 'rgba(255,255,255,0.05)'}
                                            onMouseOut={e => e.currentTarget.style.background = showEmojiPicker ? 'rgba(139, 92, 246, 0.2)' : 'transparent'}
                                        >
                                            😊
                                        </button>

                                        {showEmojiPicker && (
                                            <div style={{
                                                position: 'absolute', bottom: '100%', left: '0', marginBottom: '10px',
                                                background: '#1e293b', border: '1px solid var(--border-color)',
                                                borderRadius: '12px', padding: '12px', display: 'grid',
                                                gridTemplateColumns: 'repeat(6, 1fr)', gap: '8px',
                                                boxShadow: '0 10px 25px rgba(0,0,0,0.5)', zIndex: 10,
                                            }}>
                                                {['📈', '📉', '💰', '🚀', '⚠️', '🔥', '👍', '🙏', '🤝', '💎', '🧐', '💡', '📊', '📅', '💬', '😊', '😭', '🤯'].map(emoji => (
                                                    <button
                                                        key={emoji} type="button"
                                                        onClick={() => { insertText(emoji); setShowEmojiPicker(false); }}
                                                        style={{
                                                            fontSize: '20px', background: 'transparent', border: 'none',
                                                            cursor: 'pointer', padding: '4px', borderRadius: '6px',
                                                        }}
                                                        onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                                                        onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                                                    >
                                                        {emoji}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <textarea
                                        ref={textareaRef}
                                        value={content}
                                        onChange={(e) => setContent(e.target.value)}
                                        placeholder="Partagez vos analyses, questions ou avis..."
                                        required
                                        style={{
                                            width: '100%', minHeight: '200px', padding: '16px',
                                            backgroundColor: 'rgba(0,0,0,0.15)',
                                            color: 'var(--text-main)',
                                            border: '1px solid var(--border-color)',
                                            borderRadius: '12px', resize: 'vertical',
                                            fontFamily: 'inherit', fontSize: '14px',
                                            lineHeight: 1.6, outline: 'none',
                                            transition: 'border-color 0.2s ease',
                                            boxSizing: 'border-box',
                                        }}
                                        onFocus={e => e.currentTarget.style.borderColor = activeCat.color}
                                        onBlur={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
                                    />
                                </div>
                            </div>

                            {/* Preview active category */}
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: '10px',
                                padding: '12px 16px', borderRadius: '10px',
                                background: `${activeCat.color}10`,
                                border: `1px solid ${activeCat.color}20`,
                                marginBottom: '24px', fontSize: '13px', color: 'var(--text-muted)',
                            }}>
                                <span style={{ fontSize: '16px' }}>{activeCat.icon}</span>
                                <span>Sera publié dans la catégorie <strong style={{ color: activeCat.color }}>{activeCat.label}</strong></span>
                                {stockSymbol && <span style={{ color: '#38bdf8', fontWeight: 700 }}>· {stockSymbol}</span>}
                            </div>

                            {/* Submit */}
                            <button
                                type="submit"
                                disabled={submitting || !title.trim() || !content.trim() || (category === 'ACTION_SPECIFIQUE' && !stockSymbol)}
                                style={{
                                    width: '100%',
                                    background: (submitting || !title.trim() || !content.trim())
                                        ? 'rgba(99, 102, 241, 0.25)'
                                        : `linear-gradient(135deg, ${activeCat.color}, ${activeCat.color}bb)`,
                                    color: 'white', border: 'none',
                                    padding: '16px 24px', borderRadius: '12px',
                                    cursor: (submitting || !title.trim() || !content.trim()) ? 'not-allowed' : 'pointer',
                                    fontWeight: 700, fontSize: '15px',
                                    boxShadow: (submitting || !title.trim() || !content.trim())
                                        ? 'none'
                                        : `0 4px 20px ${activeCat.color}44`,
                                    transition: 'all 0.3s ease',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                }}
                                onMouseOver={e => {
                                    if (!submitting && title.trim() && content.trim()) {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = `0 6px 25px ${activeCat.color}55`;
                                    }
                                }}
                                onMouseOut={e => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = `0 4px 20px ${activeCat.color}44`;
                                }}
                            >
                                {submitting ? (
                                    <>⏳ Création en cours...</>
                                ) : (
                                    <>🚀 Publier la discussion</>
                                )}
                            </button>
                        </div>
                    </form>

                </div>
            </div>
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-8px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </SiteLayout>
    );
};

export default CreateTopicScreen;
